import React, { useEffect, useState } from "react";
import { mockData } from "../mock";
import { X } from "lucide-react";

const Gallery = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { images } = mockData.gallery;
  const [selectedImage, setSelectedImage] = useState(null);

  const openLightbox = (image, index) => {
    setSelectedImage({ image, index });
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateImage = React.useCallback((direction) => {
    if (!selectedImage) return;
    const newIndex =
      (selectedImage.index + direction + images.length) % images.length;
    setSelectedImage({ image: images[newIndex], index: newIndex });
  }, [selectedImage, images.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImage) {
        if (e.key === "Escape") closeLightbox();
        if (e.key === "ArrowLeft") navigateImage(-1);
        if (e.key === "ArrowRight") navigateImage(1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, navigateImage]);

  return (
    <div className="pt-20">
      <section className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <h1
            className="text-6xl md:text-7xl font-light tracking-wide text-[#3A3A3A] mb-8"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              animation: "fadeInUp 0.8s ease-out",
            }}
          >
            Gallery
          </h1>

          {/* Golden Leaf Divider */}
          <div className="flex justify-center">
            <svg
              width="80"
              height="80"
              viewBox="0 0 80 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M40 40C35 30 30 20 28 15C26 10 30 5 35 8C40 11 45 21 50 31"
                stroke="#D4AF37"
                strokeWidth="2"
                fill="none"
              />
              <path
                d="M40 40C45 30 50 20 52 15C54 10 50 5 45 8C40 11 35 21 30 31"
                stroke="#D4AF37"
                strokeWidth="2"
                fill="none"
              />
              <circle cx="40" cy="40" r="3" fill="#D4AF37" />
            </svg>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {images.map((image, index) => (
            <div
              key={index}
              className="relative overflow-hidden rounded-lg shadow-lg group cursor-pointer"
              style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.05}s both`,
              }}
              onClick={() => openLightbox(image, index)}
            >
              <img
                src={image}
                alt={`Gallery ${index + 1}`}
                className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-12 h-12 border-2 border-white rounded-full flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors duration-200"
            onClick={closeLightbox}
          >
            <X size={36} />
          </button>

          <button
            className="absolute left-6 text-white hover:text-gray-300 transition-colors duration-200 text-5xl font-light"
            onClick={(e) => {
              e.stopPropagation();
              navigateImage(-1);
            }}
          >
            ‹
          </button>

          <button
            className="absolute right-6 text-white hover:text-gray-300 transition-colors duration-200 text-5xl font-light"
            onClick={(e) => {
              e.stopPropagation();
              navigateImage(1);
            }}
          >
            ›
          </button>

          <div
            className="max-w-6xl max-h-[90vh] mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.image}
              alt={`Gallery ${selectedImage.index + 1}`}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            />
          </div>

          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-white text-sm">
            {selectedImage.index + 1} / {images.length}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Gallery;